import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const fetchCart = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setInitialized(true);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      // Don't log 401 errors - user may not be authenticated yet
      if (error.response?.status !== 401) {
        console.error('Failed to fetch cart:', error);
      }
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  // Fetch cart on mount if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !initialized) {
      fetchCart();
    } else {
      setInitialized(true);
    }
  }, [fetchCart, initialized]);

  const addToCart = async (invitationId) => {
    try {
      await axios.post(`${API_URL}/cart/add`, {
        invitation_id: invitationId
      });
      await fetchCart();
      return true;
    } catch (error) {
      throw error;
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      await axios.delete(`${API_URL}/cart/remove/${itemId}`);
      await fetchCart();
    } catch (error) {
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API_URL}/cart/clear`);
      setCart({ items: [] });
    } catch (error) {
      throw error;
    }
  };

  /**
   * Kick off checkout. For admin skip-payment flows this talks to the
   * backend immediately and returns a redirect_url. For normal purchases
   * it resolves to `{redirect_url: '/checkout'}` so the caller can route
   * the user to the Sola iFields form, which creates the pending order
   * itself via `POST /orders/checkout`.
   */
  const checkout = async (skipPayment = false) => {
    try {
      if (skipPayment) {
        const response = await axios.post(`${API_URL}/orders/checkout`, {
          skip_payment: true,
        });
        return response.data;
      }
      return { redirect_url: '/checkout' };
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        error.userMessage = detail;
      }
      console.error('Checkout error:', detail || error.message);
      throw error;
    }
  };

  const refreshCart = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchCart();
    }
  }, [fetchCart]);

  const cartTotal = cart.items?.reduce((sum, item) => sum + (item.price || 0), 0) || 0;
  const cartCount = cart.items?.length || 0;

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      fetchCart,
      addToCart,
      removeFromCart,
      clearCart,
      checkout,
      cartTotal,
      cartCount,
      refreshCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
